import { useEffect, useRef, useState } from 'react';
import { getRouteApiKey } from 'routes/api/routesApi';
import { FlowArrow } from '../useFlowGraph';
import { sendsDataBack } from './accountNav';

// API keys for the accounts that send data into Gundi: the provider always, and
// a destination once it sends data back. Fetched lazily, and only for the
// Connect section: every other section of the panel opens without spending a
// key request (the provider's used to be fetched on every Route page load,
// whatever tab the user was on).
export function useAccountApiKeys({
  token,
  providerId,
  selectedAccountId,
  openSection,
  arrows,
}: {
  token: string | undefined;
  providerId: string;
  selectedAccountId: string | undefined;
  openSection: string | undefined;
  arrows: FlowArrow[];
}) {
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  // Requested ids, not just resolved ones: `selection` is rebuilt every render
  // and `arrows` changes identity on every structural rebuild, so keying only
  // off `apiKeys` fired a second request for every render while the first was
  // still in flight. useSharedBySite guards its fetch the same way.
  const apiKeyFetched = useRef<Set<string>>(new Set());
  const selectedNeedsKey = selectedAccountId
    ? selectedAccountId === providerId ||
      sendsDataBack(selectedAccountId, arrows)
    : false;
  useEffect(() => {
    if (!token || !selectedAccountId || openSection !== 'how-to-connect')
      return;
    if (!selectedNeedsKey || apiKeyFetched.current.has(selectedAccountId))
      return;
    const id = selectedAccountId;
    apiKeyFetched.current.add(id);
    // No cancelled-guard: the write is keyed by id, so storing a late response
    // is always safe — and skipping it while the id stayed latched left the
    // section on "Loading…" for the rest of the page's life.
    getRouteApiKey(token, id)
      .then((r: any) => {
        if (r?.api_key) {
          setApiKeys((prev) => ({ ...prev, [id]: r.api_key }));
          return;
        }
        // No key and no throw: a swallowed 401 resolves undefined. Un-mark
        // it so reopening the section after logging back in tries again,
        // instead of showing "Loading…" until a full reload.
        apiKeyFetched.current.delete(id);
      })
      .catch((e: any) => {
        apiKeyFetched.current.delete(id);
        console.error('failed to load API key for account', id, e);
      });
  }, [token, selectedAccountId, openSection, selectedNeedsKey]);

  return apiKeys;
}
